import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  DatePicker,
  Divider,
  Input,
  message,
  Select,
  Spin,
} from "antd";
import dayjs from "dayjs";
import { UserOutlined } from "@ant-design/icons";
import {
  EditPencil,
  CalenderIcon,
  FormCityIcon,
  FormCountryIcon,
  FormDateIcon,
  FormEmailAddressIcon,
  FormGenderIcon,
  FormHandednessIcon,
  FormHeightIcon,
  FormHouseIcon,
  FormLanguageIcon,
  FormNameIcon,
  FormPhoneIcon,
  FormStreetIcon,
  FormWeightIcon2,
  FormZipCodeIcon,
  RoleIcon,
  SelectedTick,
  LeftHandedImage,
  RightHandedImage,
  StrikerRole,
  CoachImage,
  LocationPinIcon,
  PasswordIconNewUser,
  BasicInfoIcon,
  ContactInfoIcon,
  NewUsercalendarIcon,
} from "icons";
import { FormLabel } from "../form-label";
import { RoleOption } from "../role-option";
import { CustomSelect } from "components/custom-select";
import { ConditionalRenderer } from "components";
import { useCurrentUserContext } from "providers";
import { ROLE, RoleId } from "utils/constants";
import {
  formInitialData,
  genderOptions,
  handednessOptions,
  heightOptions,
  languages,
  PLAN_KEYS,
  planDuration,
  planTypes,
  platformItems,
  roleOptions,
  weightOptions,
} from "./constants";
import { UseApiCall, UseLazyApiCall } from "hooks";
import { ReactComponent as DropdownArrow } from "icons/dropdownArrow.svg";
import { TickIcon } from "pages/settings/icons";
import { getInitialLetters } from "utils/helpers";
import styles from "./styles.module.scss";

export const AddUserModalV2 = ({
  getUsers,
  closeModal,
  page,
  size,
  search,
}) => {
  const { user } = useCurrentUserContext();
  const { Option } = Select;

  const [imageBlobUrl, setImageBlobUrl] = useState(null);
  const [imagePublicUrl, setImagePublicUrl] = useState("");

  const [
    modifySubscription,
    {
      data: modifySubscriptionData,
      isLoading: modifySubscriptionLoading,
      modifySubscriptionError,
    },
  ] = UseLazyApiCall({
    url: "/internal/editSubscription",
    method: "POST",
  });

  const [
    uploadImage,
    {
      data: uploadImageData,
      isLoading: uploadingImage,
      error: uploadingImageError,
    },
  ] = UseLazyApiCall({
    url: "/user/uploadMedia",
    method: "POST",
    isBioMechanics: false,
  });
  const [
    addUser,
    { data: addUserData, isLoading: addUserLoading, error: addUserError },
  ] = UseLazyApiCall({
    url: "/user/register",
    method: "POST",
    isBioMechanics: false,
  });

  useEffect(() => {
    if (addUserError?.response?.data?.message) {
      message.error(addUserError?.response?.data?.message);
    }
  }, [addUserError]);

  const { data: countries, isLoading: countriesLoading } = UseApiCall({
    url: "/user/countries",
    method: "GET",
  });

  const [
    getCitiesList,
    { data: cities, isLoading: citiesloading, error: citiesError },
  ] = UseLazyApiCall({
    url: "/user/citiesByCountry",
    method: "POST",
  });

  const [platformData, setPlatformData] = useState(platformItems);

  const [userFormData, setUserFormData] = useState({
    ...formInitialData,
  });

  useEffect(() => {
    setUserFormData({
      ...userFormData,
      contactNumberCountryId: countries?.[0].id,
      // contactNumberCountryCode: countries?.[0].phoneCode,
    });
  }, [countries]);
  const [selectedCountry, setSelectedCountry] = useState({
    value: countries?.[0]?.id,
    label: (
      <span>
        <img
          src={countries?.[0]?.image}
          alt="flag"
          height={18}
          width={18}
          style={{ marginRight: 8 }}
        />
        {countries?.[0]?.phoneCode}
      </span>
    ),
  });

  useEffect(() => {
    setSelectedCountry({
      value: countries?.[0]?.id,
      label: (
        <span>
          <img
            src={countries?.[0]?.image}
            alt="flag"
            width={18}
            height={18}
            style={{ marginRight: 8, verticalAlign: "middle" }}
          />
          +{countries?.[0]?.phoneCode}
        </span>
      ),
    });
  }, [countries]);

  const handleChange = (event, data) => {
    const { value } = data;

    setSelectedCountry(event);

    setUserFormData({
      ...userFormData,
      contactNumberCountryId: `${value}`,
    });
  };

  async function handleSubmitForm(e) {
    e.preventDefault();
    const payload = { ...userFormData, picture: imagePublicUrl };

    const {
      // contactNumberCountryCode,
      genderName,
      language,
      playerTypeLabel,
      ...remainingParams
    } = payload;

    try {
      const response = await addUser({
        params: remainingParams,
      });

      if (response.message === "Success") {
        message.success("User Created Successfully");

        if (
          userFormData?.plan.key === PLAN_KEYS.BASIC ||
          userFormData?.plan.key === PLAN_KEYS.STANDARD
        ) {
          const params = {
            userId: response?.userId,
            email: user?.email,
            newExpiryMonths: JSON.stringify(userFormData?.duration?.months),
            mode: userFormData?.plan.key?.toLowerCase(),
          };

          try {
            const response = await modifySubscription({
              params,
            });

            if (response) {
              message.success("Subscription added successfully");
              message.success(
                `Account verification link sent to ${userFormData.email}`
              );
              setUserFormData({ ...setUserFormData });
              closeModal();
              getUsers({
                page,
                size,
                search,
              });
            }
          } catch (error) {
            console.log({ error });
          }
        }
      }
    } catch (error) {
      console.log({ error });
    }
  }

  function handleChangeUnits(e, unitType) {
    setUserFormData({
      ...userFormData,
      [unitType]: e,
    });
  }
  function handleChangeLanguage(id, { label, value, key }) {
    setUserFormData({
      ...userFormData,
      language: label,
      languageId: key,
    });
  }
  function handleFileSelect(event) {
    const fileInput = event.target.files[0];

    const imagePath = `m2c_user_${Math.floor(Math.random() * 1000)}`;

    const currentTimeSatmp = new Date().getTime();
    const imageExt = fileInput?.name?.split(".").pop();
    const imageTitle = `${imagePath}_${currentTimeSatmp}.${imageExt}`;
    const imageFile = new File([fileInput], imageTitle, {
      type: fileInput?.type,
    });

    setImageBlobUrl(URL.createObjectURL(imageFile));

    imageSaveHandler({
      fileName: imageTitle,
      mimeType: imageFile.type,
      file: imageFile,
    });
  }
  async function imageSaveHandler({ fileName, mimeType, file }) {
    const response = await uploadImage({
      params: {
        fileName,
        fileMimeType: mimeType,
      },
    });

    if (response && Object.keys(response).length) {
      const myHeaders = new Headers({ "Content-Type": "image/png" });
      const obj = {
        method: "PUT",
        headers: myHeaders,
        body: file,
      };

      await fetch(response?.signedUrl, obj).then((uploadResponse) => {
        if (uploadResponse.ok) {
          setImagePublicUrl(response?.publicUrl);
        } else {
          console.error("Failed to upload image.");
        }
      });
    }
  }

  function handleChangePlatform(clickedItem) {
    const newdata = platformData?.map((itemObj) =>
      clickedItem.title === itemObj.title
        ? { ...itemObj, isActive: true }
        : { ...itemObj, isActive: false }
    );

    setPlatformData(newdata);
  }

  const selectedOption = useMemo(
    () => platformData?.find((item) => item?.isActive),
    [platformData]
  );

  function getDuration(planType) {
    return planType === PLAN_KEYS.TRIAL || planType === PLAN_KEYS.PREMIUM
      ? {}
      : planDuration[1];
  }

  function handleClickOption(option) {
    setUserFormData((prev) => {
      return {
        ...prev,
        plan: option,
        duration: getDuration(option.key),
      };
    });
  }

  function handleChangeForm(e) {
    const { name, value } = e.target;
    setUserFormData((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  }
  const onChangeDate = (date, dateString) => {
    setUserFormData({ ...userFormData, dateOfBirth: dateString });
  };

  async function handleChangeCountry(id, { label, value, key, image }) {
    try {
      const res = await getCitiesList({
        params: {
          countryId: key,
        },
      });

      setUserFormData({
        ...userFormData,
        countryId: key,
        city: res[0].id,
        countryName: label,
        cityName: res[0].name,
        countryImage: image,
      });
    } catch (error) {
      console.log({ error });
    }
  }

  function handleChangeCity(id, { label, value, key }) {
    setUserFormData({
      ...userFormData,
      cityName: label,
      city: key,
    });
  }
  function handleChangeGender(id, { label, value, key }) {
    setUserFormData({
      ...userFormData,
      genderName: label,
      genderId: key,
    });
  }
  function handleChangeCountryCode(id, { label, value, key }) {
    setUserFormData({
      ...userFormData,
      contactNumberCountryId: key,
      // contactNumberCountryCode: label,
    });
  }
  function handleChangeHandedness(id, { label, value, key }) {
    setUserFormData({
      ...userFormData,
      playerType: key,
      playerTypeLabel: label,
    });
  }

  function resetForm() {
    setUserFormData(formInitialData);
  }
  function handleClickDuration(option) {
    setUserFormData((prev) => ({
      ...prev,
      duration: option,
    }));
  }
  const addUserDisabled =
    !userFormData.email || !userFormData.firstName || !userFormData.lastName;

  return (
    <div className={styles.addUserContainer}>
      <h3 className={styles.heading}>Add New User</h3>

      <form onSubmit={handleSubmitForm} className={styles.formTag}>
        <div className={styles.form}>
          <div className={styles.section1}>
            <div className={styles.basicInfoHeader}>
              <BasicInfoIcon />
              <h3>Basic Information</h3>
            </div>
            <Divider style={{ margin: "0" }} />
            <div className={styles.personalInfo}>
              <div className={styles.DP}>
                <label
                  htmlFor="file"
                  style={{ cursor: "pointer", position: "relative" }}
                >
                  <ConditionalRenderer
                    condition={
                      imageBlobUrl ||
                      (!userFormData.firstName && !userFormData.lastName)
                    }
                  >
                    <div className={styles.image}>
                      <Avatar
                        size={70}
                        src={imageBlobUrl}
                        alt=""
                        icon={<UserOutlined />}
                      />
                    </div>
                  </ConditionalRenderer>
                  <ConditionalRenderer
                    condition={
                      (userFormData.firstName || userFormData.lastName) &&
                      !imageBlobUrl
                    }
                  >
                    <div className={styles.imageInitials}>
                      {getInitialLetters(
                        `${userFormData.firstName} ${userFormData?.lastName}`
                      )}
                    </div>
                  </ConditionalRenderer>

                  <div className={styles.editIcon}>
                    <EditPencil height={15} width={15} />
                  </div>
                  <input
                    type="file"
                    id="file"
                    style={{ display: "none" }}
                    accept=".png, .jpg, jpeg"
                    onChange={handleFileSelect}
                  />
                </label>
                <div className={styles.profileContainer}>
                  <div className={styles.name}>
                    {userFormData.firstName} {userFormData.lastName}
                  </div>
                  <div
                    className={styles.handedness}
                    style={{ backgroundColor: "#c7e7ff" }}
                  >
                    {RoleId[userFormData.role] === ROLE.Striker ? (
                      <StrikerRole height={17} width={17} />
                    ) : (
                      <CoachImage height={17} width={17} />
                    )}
                    <span> {RoleId[userFormData.role]} </span>
                  </div>
                  <div
                    className={styles.handedness}
                    style={{ backgroundColor: "#16A6654D" }}
                  >
                    {userFormData.playerTypeLabel === "Left Handed" ? (
                      <LeftHandedImage height={17} width={13.5} />
                    ) : (
                      <RightHandedImage height={17} width={13.5} />
                    )}
                    <span>{userFormData.playerTypeLabel}</span>
                  </div>
                </div>
              </div>

              <ConditionalRenderer condition={userFormData?.countryName}>
                <div className={styles.location}>
                  <LocationPinIcon />
                  {userFormData?.cityName}, {userFormData?.countryName}
                  {/* <SwitzerlandIcon /> */}
                  <img
                    src={userFormData?.countryImage}
                    alt="countryImage"
                    height={24}
                    width={24}
                  />
                </div>
              </ConditionalRenderer>
            </div>
            <div className={styles.flexOrGrid}>
              {/* <div className={styles.DP}>
                <label
                  htmlFor="file"
                  style={{ cursor: "pointer", position: "relative" }}
                >
                  <div className={styles.image}>
                    <Avatar
                      size={75}
                      src={imageBlobUrl}
                      alt=""
                      icon={<UserOutlined />}
                    />
                  </div>
                  <div className={styles.editIcon}>
                    <EditPencil />
                  </div>
                  <input
                    type="file"
                    id="file"
                    style={{ display: "none" }}
                    accept=".png, .jpg, jpeg"
                    onChange={handleFileSelect}
                  />
                </label>
              </div> */}
              <div>
                <FormLabel label="First Name" required Icon={FormNameIcon} />
                <Input
                  style={{ height: "36px" }}
                  type="text"
                  placeholder="Enter First Name"
                  required
                  value={userFormData.firstName}
                  onChange={handleChangeForm}
                  name="firstName"
                  maxLength={50}
                />
              </div>
              <div>
                <FormLabel label="Last Name" required Icon={FormNameIcon} />
                <Input
                  style={{ height: "36px" }}
                  type="text"
                  placeholder="Enter Last Name"
                  required
                  value={userFormData.lastName}
                  onChange={handleChangeForm}
                  name="lastName"
                  maxLength={50}
                />
              </div>
            </div>
            <div className={styles.flexOrGrid}>
              <div>
                <FormLabel
                  label="Email Address"
                  required
                  Icon={FormEmailAddressIcon}
                />
                <Input
                  style={{ height: "36px" }}
                  type="email"
                  placeholder="Enter email"
                  required
                  value={userFormData.email}
                  onChange={handleChangeForm}
                  name="email"
                  maxLength={250}
                />
              </div>
              <div>
                <FormLabel label="Password" Icon={PasswordIconNewUser} />
                <Input.Password
                  style={{ height: "36px" }}
                  type="password"
                  placeholder="Enter password"
                  value={userFormData.password}
                  onChange={handleChangeForm}
                  name="password"
                />
              </div>
            </div>
            <div>
              <FormLabel label="Role" required Icon={RoleIcon} />

              <div className={styles.roleOptions}>
                {roleOptions?.map((item) => (
                  <div
                    key={item.id}
                    onClick={() =>
                      setUserFormData({ ...userFormData, role: item.id })
                    }
                    style={{ width: "50%" }}
                  >
                    <RoleOption
                      label={item.label}
                      Icon={item.icon}
                      isActive={userFormData.role === item.id}
                    />
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.flexOrGrid}>
              <div>
                <FormLabel label="Phone Number" Icon={FormPhoneIcon} />
                <div style={{ display: "flex", gap: "5px" }}>
                  <Select
                    style={{ width: 150, height: 36 }}
                    labelInValue
                    value={selectedCountry}
                    onChange={handleChange}
                    optionLabelProp="label"
                    suffixIcon={<DropdownArrow height={6.3} width={11.8} />}
                  >
                    {countries?.map((country) => (
                      <Option
                        key={country.id}
                        value={country.id}
                        label={
                          <span
                            style={{
                              display: "flex",
                              alignItems: "center",
                              height: "30px",
                            }}
                          >
                            <img
                              src={country.image}
                              alt="flag"
                              width={18}
                              height={18}
                              style={{
                                marginRight: 8,
                                verticalAlign: "middle",
                              }}
                            />
                            +{country.phoneCode}
                          </span>
                        }
                      >
                        <span
                          role="img"
                          aria-label={country.name}
                          style={{
                            // display: "flex",
                            // alignItems: "center",
                            // height: "30px",
                            marginRight: "8px",
                            verticalAlign: "middle",
                          }}
                        >
                          <img
                            src={country.image}
                            alt="flag"
                            width={18}
                            height={18}
                          />
                        </span>
                        {country.phoneCode}
                      </Option>
                    ))}
                  </Select>

                  <Input
                    style={{ height: "36px" }}
                    type="text"
                    placeholder="xxx-xxx-xxx"
                    value={userFormData.contactNumer}
                    onChange={handleChangeForm}
                    name="contactNumer"
                    maxLength={15}
                  />
                </div>
              </div>
              <div>
                <FormLabel label="Language" Icon={FormLanguageIcon} />
                <Select
                  style={{ width: "100%", height: "36px" }}
                  placeholder="Select Language"
                  value={userFormData?.language}
                  suffixIcon={<DropdownArrow height={14} width={13} />}
                  onChange={handleChangeLanguage}
                >
                  {languages?.map((language) => (
                    <Option
                      key={language.id}
                      value={language.id}
                      label={language.name}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {language.name}
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
            <div className={styles.flexOrGrid}>
              <div>
                <FormLabel label="Gender" Icon={FormGenderIcon} />
                <Select
                  style={{ width: "100%", height: "36px" }}
                  placeholder="Select Gender"
                  value={userFormData?.genderName}
                  suffixIcon={<DropdownArrow height={14} width={13} />}
                  onChange={handleChangeGender}
                >
                  {genderOptions?.map((gender) => (
                    <Option
                      key={gender.key}
                      value={gender.key}
                      label={gender.label}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {gender.label}
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>
              <div>
                <FormLabel label="Date of Birth" Icon={FormDateIcon} />
                {/* <Input
              style={{ height: "36px" }}
              type="date"
              placeholder="Select Date"
              value={userFormData.dateOfBirth}
            /> */}
                <DatePicker
                  name="dateOfBirth"
                  value={
                    userFormData?.dateOfBirth &&
                    dayjs(userFormData?.dateOfBirth)
                  }
                  onChange={onChangeDate}
                  style={{ height: "36px", width: "100%" }}
                  suffixIcon={<NewUsercalendarIcon />}
                />
              </div>
            </div>

            <div>
              <FormLabel
                label="Handedness"
                Icon={FormHandednessIcon}
                required
              />

              <Select
                style={{ width: "100%", height: "36px" }}
                placeholder="Select Gender"
                value={userFormData?.playerType}
                suffixIcon={<DropdownArrow height={14} width={13} />}
                onChange={handleChangeHandedness}
              >
                {handednessOptions?.map((Handedness) => (
                  <Option
                    key={Handedness.key}
                    value={Handedness.key}
                    label={Handedness.label}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      {Handedness.label}
                    </div>
                  </Option>
                ))}
              </Select>
            </div>
            <div className={styles.flexOrGrid}>
              <div>
                <FormLabel label="Height" Icon={FormHeightIcon} />
                <div style={{ display: "flex", gap: "5px" }}>
                  <CustomSelect
                    defaultValue={"cm"}
                    optionsArray={heightOptions}
                    style={{
                      select: {
                        width: "80px",
                        height: "36px",
                      },
                    }}
                    variant="user-detail"
                    onChange={(e) => handleChangeUnits(e, "heightUnit")}
                    iconHeight={14}
                    iconWidth={13}
                  />
                  <Input
                    style={{ height: "36px" }}
                    type="text"
                    placeholder="Height"
                    value={userFormData.height}
                    onChange={handleChangeForm}
                    name="height"
                    maxLength={10}
                  />
                </div>
              </div>
              <div>
                <FormLabel label="Weight" Icon={FormWeightIcon2} />
                <div style={{ display: "flex", gap: "5px" }}>
                  <CustomSelect
                    defaultValue={"kg"}
                    optionsArray={weightOptions}
                    style={{
                      select: {
                        width: "80px",
                        height: "36px",
                      },
                    }}
                    variant="user-detail"
                    onChange={(e) => handleChangeUnits(e, "weightUnit")}
                    iconHeight={14}
                    iconWidth={13}
                  />
                  <Input
                    style={{ height: "36px" }}
                    type="text"
                    placeholder="Weight"
                    value={userFormData.weight}
                    onChange={handleChangeForm}
                    name="weight"
                    maxLength={10}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className={styles.section2}>
            <div className={styles.contactInfo}>
              <div className={styles.contactInfoHeader}>
                <ContactInfoIcon />
                <h3>Contact Information</h3>
              </div>
              <Divider style={{ margin: "0" }} />
              <div className={styles.flexOrGrid}>
                <div>
                  <FormLabel label="Street" Icon={FormStreetIcon} />
                  <Input
                    style={{ height: "36px" }}
                    type="text"
                    placeholder="Enter Street No."
                    value={userFormData.street}
                    onChange={handleChangeForm}
                    name="street"
                    maxLength={150}
                  />
                </div>
                <div>
                  <FormLabel label="House Number" Icon={FormHouseIcon} />
                  <Input
                    style={{ height: "36px" }}
                    type="text"
                    placeholder="Enter House Number"
                    value={userFormData.houseNumber}
                    onChange={handleChangeForm}
                    name="houseNumber"
                    maxLength={20}
                  />
                </div>
              </div>
              <div className={styles.flexOrGrid}>
                <div>
                  <FormLabel label="Zip Code" Icon={FormZipCodeIcon} />
                  <Input
                    style={{ height: "36px" }}
                    type="text"
                    placeholder="Enter Zip Code"
                    value={userFormData.postCode}
                    onChange={handleChangeForm}
                    name="postCode"
                  />
                </div>
                <div>
                  <FormLabel label="Country" Icon={FormCountryIcon} />

                  <Select
                    showSearch
                    style={{ width: "100%", height: "36px" }}
                    placeholder="Select a country"
                    optionFilterProp="label"
                    value={userFormData?.countryName}
                    suffixIcon={<DropdownArrow height={14} width={13} />}
                    onChange={handleChangeCountry}
                  >
                    {countries?.map((country) => (
                      <Option
                        key={country.id}
                        value={country.id}
                        label={country.name}
                        image={country?.image}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <img
                            src={country.pngImage}
                            alt={country.name}
                            style={{ width: 20, marginRight: 8 }}
                          />
                          {country.name}
                        </div>
                      </Option>
                    ))}
                  </Select>
                </div>
              </div>
              <div>
                <FormLabel label="City" Icon={FormCityIcon} />
                <Select
                  showSearch
                  style={{ width: "100%", height: "36px" }}
                  placeholder="Select a city"
                  optionFilterProp="label"
                  value={userFormData?.cityName}
                  suffixIcon={<DropdownArrow height={14} width={13} />}
                  onChange={handleChangeCity}
                >
                  {cities?.map((city) => (
                    <Option key={city.id} value={city.id} label={city.name}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        {city.name}
                      </div>
                    </Option>
                  ))}
                </Select>
              </div>
            </div>
            <div className={styles.extendPlan}>
              <div className={styles.header}>
                <div className={styles.icon}>€</div>
                <h3 className={styles.title}>
                  Select Subscription<span style={{ color: "red" }}>*</span>
                </h3>
              </div>

              <div className={styles.planSelection}>
                <div className={styles.planOptions}>
                  {planTypes?.map((item) => {
                    const isActive = userFormData?.plan?.key === item.key;

                    return (
                      <div
                        className={`${styles.singlePlanOption} ${
                          isActive && styles.active
                        }`}
                        onClick={() => handleClickOption(item)}
                      >
                        <div key={item.key} className={styles.titleAndRadio}>
                          <div className={styles.title}>{item.title}</div>
                          <ConditionalRenderer condition={isActive}>
                            <SelectedTick />
                          </ConditionalRenderer>
                          <ConditionalRenderer condition={!isActive}>
                            <div className={`${styles.radioBtn}`}></div>
                          </ConditionalRenderer>
                        </div>
                        <div className={styles.platforms}>
                          {item?.platforms?.map((item) => (
                            <div
                              key={item.title}
                              style={{ backgroundColor: `${item.bgColor}` }}
                              className={styles.singlePlatform}
                            >
                              <item.icon height={15} width={15} /> {item.title}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              <ConditionalRenderer
                condition={
                  userFormData?.plan?.key === PLAN_KEYS.BASIC ||
                  userFormData?.plan?.key === PLAN_KEYS.STANDARD
                }
              >
                <div className={styles.selectContainer}>
                  <h3>Select Duration</h3>

                  <div className={styles.planOptions}>
                    {planDuration?.map((item) => {
                      const isActive = userFormData?.duration.key === item.key;

                      return (
                        <div
                          key={item.key}
                          className={`${styles.singlePlanOption} ${
                            isActive && styles.active
                          }`}
                          onClick={() => handleClickDuration(item)}
                        >
                          <div className={styles.title}>{item.title}</div>
                          <ConditionalRenderer condition={isActive}>
                            <SelectedTick />
                          </ConditionalRenderer>
                          <ConditionalRenderer condition={!isActive}>
                            <div className={`${styles.radioBtn}`}></div>
                          </ConditionalRenderer>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </ConditionalRenderer>
              <ConditionalRenderer
                condition={userFormData?.plan?.key === PLAN_KEYS.PREMIUM}
              >
                <div className={styles.licenseLabel}>Enter License Key</div>

                <div className={styles.license}>
                  <Input
                    style={{ height: "36px" }}
                    type="text"
                    placeholder="e.g 97UG8473VH"
                    value={userFormData.license}
                    onChange={handleChangeForm}
                    name="license"
                    maxLength={10}
                    suffix={<TickIcon height={15} width={15} />}
                  />
                  <button className={addUserDisabled ? styles.disabled : ""}>
                    Redeem
                  </button>
                </div>
                <ConditionalRenderer
                  condition={RoleId[userFormData.role] !== "Coach"}
                >
                  <span style={{ color: "red" }}>
                    Role should be coach to redeem License Key
                  </span>
                </ConditionalRenderer>
              </ConditionalRenderer>
            </div>
          </div>
        </div>
        <div
          className={`${styles.btn} ${addUserDisabled ? styles.disabled : ""}`}
        >
          <button type="submit">Add User</button>
        </div>
      </form>
    </div>
  );
};
